"use client";

import { usePathname } from "next/navigation";
import type { ReactNode } from "react";
import { AuthGate } from "@/components/AuthGate";
import { Sidebar } from "@/components/Sidebar";
import { SeedLoader } from "@/components/SeedLoader";
import { ClientProvider } from "@/context/ClientContext";
import { ClientBrandApplier } from "@/components/ClientBrandApplier";

/**
 * Routes that render with no Backchannel chrome at all — no auth check, no
 * sidebar, no client context.
 *
 * These are pages shared with people who do not have a Backchannel account.
 * `AuthGate` alone is not enough: `ClientProvider` and `SeedLoader` both issue
 * Convex queries that assume a signed-in agency user, and the sidebar is
 * meaningless to someone who arrived from an emailed link.
 */
const BARE_PATHS = ["/assess"];

function isBarePath(pathname: string): boolean {
  return BARE_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

export function AppShell({ children }: { children: ReactNode }) {
  const pathname = usePathname();

  if (isBarePath(pathname)) {
    return <>{children}</>;
  }

  return (
    <AuthGate>
      <ClientProvider>
        <ClientBrandApplier />
        <SeedLoader />
        <Sidebar />
        <div className="min-h-screen pt-16 md:pl-60 md:pt-0">
          <main className="min-h-screen">{children}</main>
        </div>
      </ClientProvider>
    </AuthGate>
  );
}
